// The core's block editor of long texts, as in Notion (decision 034, um/2026-10-30/03 п.2 и п.11): free writing without a box, blocks
// dragged by the handle, «/» and «+» for a block's kind. The text in and out is markdown — the site, letters and versions read it as is.

import { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { newBlock, parseBlocks, recordTitle, serializeBlocks, uuidv7, type Block, type BlockKind } from '@confdeck/core-shared';
import { useMeta, useSync } from '../sync/react.tsx';
import { Icon } from '../ui/icons.tsx';
import { Popover, PopoverAnchor, PopoverContent, cx } from '../ui/primitives.tsx';
import { PickList, type PickOption } from '../ui/PickList.tsx';
import { setCaret } from './caret.ts';
import { EditorCtl } from './ctl.ts';
import { applyKind } from './keys.ts';
import { findKinds, KINDS } from './kinds.ts';
import { BlockRow, type RowEnv } from './BlockRow.tsx';
import p from '../fields/fields.module.css';
import s from './editor.module.css';

/** Files of the text: the address a file will have, and sending its bytes once the text that names it is saved. */
export interface EditorFiles { urlFor: (file: File, slot: string) => string; send: (file: File, slot: string) => Promise<boolean> }

export interface BlockEditorProps {
  value: string;
  /** The new markdown; `now` — save at once (a file is about to go to the server). */
  onChange: (md: string, now?: boolean) => void;
  label: string;
  readOnly?: boolean;
  placeholder?: string;
  files?: EditorFiles;
  onOpenRecord?: (id: string) => void;
  /** The kinds of blocks this text offers (kinds.ts: a letter, a site's text, a line); all by default. */
  kinds?: BlockKind[];
  /** The caret goes into the first line when the editor appears. */
  autoFocus?: boolean;
  /** An empty first line above what the text starts with (a reply above the quote and the signature). */
  leadLine?: boolean;
  /** A smaller text without the gutter's indent: inside a form or a side panel. */
  compact?: boolean;
  /** True while the owner has not saved this text: a text that came from elsewhere waits instead of replacing it. */
  hold?: () => boolean;
  /** The editor took a text that came from elsewhere. */
  onTake?: () => void;
}

export function BlockEditor({ value, onChange, label, readOnly = false, placeholder, files, onOpenRecord, kinds, autoFocus, leadLine, compact, hold, onTake }: BlockEditorProps) {
  const client = useSync();
  const meta = useMeta();
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const ctl = useMemo(() => {
    const blocks = parseBlocks(value);
    return new EditorCtl(leadLine && blocks.length && !(blocks[0]!.kind === 'p' && !blocks[0]!.text) ? [newBlock('p'), ...blocks] : blocks);
  }, []);
  // Without a place for files there is no «Картинка или файл» to offer.
  ctl.kinds = useMemo(() => (kinds || !files ? new Set((kinds ?? KINDS.map((k) => k.kind)).filter((k) => k !== 'file' || files)) : null), [kinds?.join(), !files]);
  const hint = placeholder ?? (ctl.kinds && ctl.kinds.size < 2 ? 'Напишите текст' : 'Напишите текст или «/» для команд');
  const root = useRef<HTMLDivElement>(null);
  const focused = useRef(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [menu, setMenu] = useState<{ id: string; what: 'menu' | 'record' } | null>(null);
  const [drag, setDrag] = useState<{ id: string; over: string | null; pos: 'before' | 'after' } | null>(null);
  const fileFor = useRef<string | null>(null);

  ctl.root = root.current;
  ctl.onChange = onChange;
  ctl.render = rerender;
  ctl.ask = (what, id) => {
    if (!what || !id) { setMenu(null); return; }
    if (what === 'file') { fileFor.current = id; fileInput.current?.click(); return; }
    setMenu({ id, what });
  };

  // A text changed elsewhere (another person, a restored version) comes in while nobody writes here; checked again on leaving.
  const valueRef = useRef(value);
  valueRef.current = value;
  const owner = useRef({ hold, onTake });
  owner.current = { hold, onTake };
  const takeValue = () => {
    if (focused.current || fileFor.current || owner.current.hold?.()) return;
    const parsed = parseBlocks(valueRef.current);
    if (ctl.foreign(serializeBlocks(parsed))) { ctl.reset(parsed); owner.current.onTake?.(); }
  };
  useEffect(takeValue, [value]);
  // A closed file dialog without a file lets the text come in again.
  useEffect(() => { const el = fileInput.current; const on = () => { fileFor.current = null; }; el?.addEventListener('cancel', on); return () => el?.removeEventListener('cancel', on); }, []);

  useEffect(() => { if (autoFocus && !readOnly) ctl.focus(ctl.blocks[0]!.id, 0); }, []);

  // The caret goes where an operation asked, after the blocks it names are drawn.
  useLayoutEffect(() => {
    ctl.root = root.current;
    const f = ctl.focusReq;
    if (!f) return;
    ctl.focusReq = null;
    const el = ctl.el(f.id);
    if (el && (document.activeElement !== el || f.at !== 'end')) setCaret(el, f.at);
  });

  const titleOf = (id: string) => { const r = client.store.record(id); return r ? recordTitle(r, meta) : undefined; };

  /** Files go into the text as blocks at once (a local preview), then their bytes go to the server after the text is saved. */
  const addFiles = (id: string, list: File[]) => {
    if (!files || !list.length) return;
    const made = list.map((f) => ({ f, slot: uuidv7() }));
    const blocks = made.map(({ f, slot }) => newBlock('file', f.name, { url: files.urlFor(f, slot) }));
    blocks.forEach((b, i) => ctl.previews.set(b.url!, URL.createObjectURL(made[i]!.f)));
    const b = ctl.get(id);
    const replace = b && b.kind === 'p' && !b.text.trim();
    ctl.commit(ctl.blocks.flatMap((x) => (x.id === id ? (replace ? blocks : [x, ...blocks]) : [x])), true);
    for (const [i, { f, slot }] of made.entries()) {
      void files.send(f, slot).then((ok) => {
        const url = blocks[i]!.url!;
        URL.revokeObjectURL(ctl.previews.get(url) ?? '');
        ctl.previews.delete(url);
        if (!ok) ctl.commit(ctl.blocks.filter((x) => x.id !== blocks[i]!.id)); else rerender();
      });
    }
    ctl.focus(blocks[blocks.length - 1]!.id);
  };

  /** «+»: a new line below (or this one, when empty) with the «/» list open on it. */
  const onPlus = (id: string) => {
    const b = ctl.get(id);
    const empty = !!b && b.kind === 'p' && !b.text.trim();
    const target = empty ? b! : newBlock('p');
    if (!empty) ctl.insertAfter(id, [target], 0); else ctl.focus(id, 0);
    requestAnimationFrame(() => { const t = ctl.el(target.id); if (t) { setCaret(t, 0); document.execCommand('insertText', false, '/'); } });
  };

  const env: RowEnv = { ctl, readOnly, titleOf, onOpenRecord, onFiles: addFiles, onPlus, placeholder: hint };
  const blocks = ctl.blocks;
  const solo = blocks.length === 1 && blocks[0]!.kind === 'p' && !blocks[0]!.text;
  let num = 0;

  const onDragOver = (e: React.DragEvent) => {
    if (!drag) return;
    const row = (e.target as HTMLElement).closest<HTMLElement>('[data-bid]');
    if (!row) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const box = row.getBoundingClientRect();
    const pos = e.clientY < box.top + box.height / 2 ? 'before' : 'after';
    if (drag.over !== row.dataset.bid || drag.pos !== pos) setDrag({ ...drag, over: row.dataset.bid!, pos });
  };
  const onDrop = (e: React.DragEvent) => {
    if (!drag) return;
    e.preventDefault();
    if (drag.over) ctl.moveTo(drag.id, drag.over, drag.pos);
    setDrag(null);
  };

  return (
    <div ref={root} className={cx(s.editor, readOnly && s.readOnly, compact && s.compact)} aria-label={label} role="group"
      onFocus={() => { focused.current = true; }}
      onBlur={(e) => { if (!root.current?.contains(e.relatedTarget as Node)) { focused.current = false; if (ctl.slash) { ctl.slash = null; rerender(); } takeValue(); } }}
      onDragOver={onDragOver} onDrop={onDrop}>
      {blocks.map((b) => {
        num = b.kind === 'number' ? num + 1 : 0;
        const dropAt = drag && drag.over === b.id && drag.id !== b.id ? drag.pos : null;
        return (
          <BlockRow key={b.id} b={b} num={num} env={env} solo={solo} drop={dropAt}>
            <Grip b={b} ctl={ctl} open={menu?.id === b.id ? menu.what : null} onClose={() => setMenu(null)}
              onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', ''); const row = root.current?.querySelector(`[data-bid="${b.id}"]`); if (row) e.dataTransfer.setDragImage(row, 0, 12); setDrag({ id: b.id, over: null, pos: 'after' }); }}
              onDragEnd={() => setDrag(null)} />
          </BlockRow>
        );
      })}
      {!readOnly && <div className={s.tail} onClick={() => { const last = blocks[blocks.length - 1]!; if (last.kind === 'p' && !last.text) ctl.focus(last.id, 0); else ctl.insertAfter(last.id, [newBlock('p')], 0); }} />}
      {ctl.slash && !readOnly && <SlashMenu ctl={ctl} />}
      <input ref={fileInput} type="file" hidden multiple aria-label="Файл для текста" onChange={(e) => { const id = fileFor.current; fileFor.current = null; const list = [...(e.target.files ?? [])]; e.target.value = ''; if (id) addFiles(id, list); }} />
    </div>
  );
}

/** The handle: dragged it moves the block, clicked it opens the block's menu; a record is picked from the same place. */
function Grip({ b, ctl, open, onClose, onDragStart, onDragEnd }: { b: Block; ctl: EditorCtl; open: 'menu' | 'record' | null; onClose: () => void; onDragStart: (e: React.DragEvent) => void; onDragEnd: () => void }) {
  return (
    <Popover open={!!open} onOpenChange={(o) => { if (!o) { onClose(); ctl.focus(b.id); } }}>
      <PopoverAnchor asChild>
        <button type="button" className={s.handle} tabIndex={-1} draggable aria-label="Перетащить блок или открыть его меню" title="Перетащите, чтобы переместить · нажмите — меню"
          onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={() => ctl.ask('menu', b.id)}>
          <Icon name="grip" size={15} />
        </button>
      </PopoverAnchor>
      <PopoverContent width={280} align="start">
        {open === 'record' ? <RecordPick onPick={(id, title) => { onClose(); const add = newBlock('record', title, { recordId: id }); const cur = ctl.get(b.id); ctl.commit(ctl.blocks.flatMap((x) => (x.id === b.id ? (cur?.kind === 'p' && !cur.text.trim() ? [add, newBlock('p')] : [x, add]) : [x]))); }} />
          : <BlockMenu b={b} ctl={ctl} onDone={onClose} />}
      </PopoverContent>
    </Popover>
  );
}

function BlockMenu({ b, ctl, onDone }: { b: Block; ctl: EditorCtl; onDone: () => void }) {
  const options: PickOption[] = [
    { id: 'delete', label: 'Удалить', group: 'Блок', render: <span className={s.opt}><Icon name="trash" size={14} />Удалить</span>, hint: 'Del' },
    { id: 'copy', label: 'Дублировать', group: 'Блок', render: <span className={s.opt}><Icon name="copy" size={14} />Дублировать</span> },
    { id: 'up', label: 'Выше', group: 'Блок', render: <span className={s.opt}><Icon name="chevronUp" size={14} />Выше</span>, hint: 'Ctrl+Shift+↑' },
    { id: 'down', label: 'Ниже', group: 'Блок', render: <span className={s.opt}><Icon name="chevronDown" size={14} />Ниже</span>, hint: 'Ctrl+Shift+↓' },
    ...KINDS.filter((k) => k.kind !== b.kind && ctl.allows(k.kind)).map((k) => ({ id: `kind:${k.kind}`, label: k.label, group: 'Превратить в', render: <span className={s.opt}><Icon name={k.icon} size={14} />{k.label}</span> })),
  ];
  return (
    <PickList options={options} selected={[]} placeholder="Действие или вид блока…" onDone={onDone} onPick={(id) => {
      if (id === 'delete') ctl.remove(b.id);
      else if (id === 'copy') ctl.duplicate(b.id);
      else if (id === 'up' || id === 'down') ctl.moveBy(b.id, id === 'up' ? -1 : 1);
      else setTimeout(() => ctl.turnInto(b.id, id.slice(5) as Block['kind']));
    }} />
  );
}

/** Any record the person can read, by its title (the newest sections first). */
function RecordPick({ onPick }: { onPick: (id: string, title: string) => void }) {
  const client = useSync();
  const meta = useMeta();
  const search = (q: string): PickOption[] => {
    const k = q.trim().toLowerCase();
    const out: PickOption[] = [];
    for (const t of meta.types()) {
      for (const id of client.store.recordIds(t.id)) {
        const r = client.store.record(id);
        const title = r ? recordTitle(r, meta) : '';
        if (!k || title.toLowerCase().includes(k)) out.push({ id, label: title, hint: t.name, render: <span className={s.opt}><Icon name={t.icon} size={14} />{title}</span> });
        if (out.length >= 40) return out;
      }
    }
    return out;
  };
  return <PickList options={search} selected={[]} placeholder="Найти запись…" emptyText="Записей не нашлось" onPick={(id) => onPick(id, titleOfPick(client, meta, id))} />;
}
const titleOfPick = (client: ReturnType<typeof useSync>, meta: ReturnType<typeof useMeta>, id: string) => { const r = client.store.record(id); return r ? recordTitle(r, meta) : ''; };

/** The «/» list under the caret; the keys stay in the text, the list follows them (keys.ts). */
function SlashMenu({ ctl }: { ctl: EditorCtl }) {
  const sl = ctl.slash!;
  const list = findKinds(sl.query, ctl.kinds);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.querySelector(`[data-i="${sl.active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [sl.active]);
  if (!list.length) return null;
  // Under the caret, lifted when the window is short; the visible part of the page counts on a phone with its keyboard open.
  const vh = window.visualViewport?.height ?? window.innerHeight;
  const top = Math.max(8, Math.min(sl.y + 6, vh - 340));
  const left = Math.max(8, Math.min(sl.x, window.innerWidth - 300));
  return createPortal(
    <div ref={ref} className={s.slash} style={{ top, left }} role="listbox" aria-label="Вид блока" onMouseDown={(e) => e.preventDefault()}>
      <div className={p.pickGroup}>Блоки</div>
      {list.map((k, i) => (
        <div key={k.kind} data-i={i} role="option" aria-selected={i === sl.active} className={cx(p.pickItem, i === sl.active && p.pickActive)}
          onMouseMove={() => { if (sl.active !== i) { sl.active = i; ctl.render(); } }} onClick={() => applyKind(ctl, sl.id, k.kind)}>
          <span className={s.slashIcon}><Icon name={k.icon} size={16} /></span>
          <span className={s.slashText}><span className={p.pickLabel}>{k.label}</span><span className={s.slashHint}>{k.hint}</span></span>
        </div>
      ))}
    </div>,
    document.body,
  );
}
